import {useState} from 'react';
import AppContext from './AppContext';
import Environment from '../config/Environment';
import Storage from '../helpers/Storage';
import {ShoppingCart} from '../models/ShoppingCart';

function AppProvider({children}) {
    const client = Environment.client;
    const [store, setStore] = useState(Storage.getStore());
    const [shoppingCart, setShoppingCart] = useState(Storage.getShoppingCart());
    const sentryErrorTestingEnvironments = ['development', 'staging'];

    const _setStore = (store) => {
        Storage.setStore(store);
        setStore(store);
    };

    const newShoppingCart = () => {
        const id = `${client}-cart-${new Date().getTime()}`;
        return new ShoppingCart({id: id, store: store, client: client, items: []});
    };

    const createShoppingCart = () => {
        const _newShoppingCart = newShoppingCart();
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const deleteShoppingCart = () => {
        Storage.removeShoppingCart();
        setShoppingCart(undefined);
    };

    const clearShoppingCart = () => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        _newShoppingCart.clear();
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const updateShoppingCartStatus = (status) => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        _newShoppingCart.status = status;
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const addItemToCart = (item) => {
        let _newShoppingCart;
        if (shoppingCart) _newShoppingCart = shoppingCart.clone();
        else _newShoppingCart = newShoppingCart();
        _newShoppingCart.addItem(item);
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const updateItemsStatus = (invalidIds) => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        _newShoppingCart.items.forEach((item) => {
            if (invalidIds.includes(item.id)) _newShoppingCart.markItemAsDeleted(item.id);
            else _newShoppingCart.markItemAsActive(item.id);
        });
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const removeItemsFromCart = (ids) => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        ids.forEach((id) => {_newShoppingCart.deleteItem(id)});
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const removeMarkedItemsFromCart = () => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        let ids = [];
        _newShoppingCart.items.forEach((item) => {
            if (item.status === 'deleted') ids.push(item.id);
        });
        ids.forEach((id) => {_newShoppingCart.deleteItem(id)});
        _newShoppingCart.status = 'updated';
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const replaceAllItemsInCart = (newItem, newStore) => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        _newShoppingCart.clear();
        if (newStore) _newShoppingCart.store = newStore;
        _newShoppingCart.addItem(newItem);
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const setCartItemQuantity = (id, quantity) => {
        if (!shoppingCart) return;
        const _newShoppingCart = shoppingCart.clone();
        const index = _newShoppingCart.items.findIndex(item => item.id === id);
        if (index < 0) return;
        _newShoppingCart.items[index].setQuantity(quantity);
        Storage.setShoppingCart(_newShoppingCart);
        setShoppingCart(_newShoppingCart);
    };

    const value = {
        showVersionTag: true,
        throwExceptionOnVersionClick: sentryErrorTestingEnvironments.includes(Environment.current), // This must be disabled in production, it is only used for testing the Sentry integration.
        navigationBarHeight: 90,
        qrScannerMessageHeight: 150,
        qrScannerOffsetHeight: 35,
        productScanButtonHeight: 56,
        productAddToCartButtonHeight: 56,
        cartScanButtonHeight: 56,
        cartPayButtonHeight: 56,
        pricesBoxMaxHeight: 176,
        identificationNextButtonHeight: 56,
        addressCopyNextButtonHeight: 56,
        cartMode: 'single', // 'full' for up to [fullCartModeMaxItems] products or 'single' for a just one product.
        fullCartModeMaxItems: 30,
        store: store,
        setStore: _setStore,
        shoppingCart: shoppingCart,
        setShoppingCart: setShoppingCart,
        createShoppingCart: createShoppingCart,
        deleteShoppingCart: deleteShoppingCart,
        clearShoppingCart: clearShoppingCart,
        updateShoppingCartStatus: updateShoppingCartStatus,
        addItemToCart: addItemToCart,
        updateItemsStatus: updateItemsStatus,
        removeItemsFromCart: removeItemsFromCart,
        removeMarkedItemsFromCart: removeMarkedItemsFromCart,
        replaceAllItemsInCart: replaceAllItemsInCart,
        setCartItemQuantity: setCartItemQuantity
    };

    return (
        <AppContext.Provider value={value}>{children}</AppContext.Provider>
    );
}

export default AppProvider;